import { connect } from 'react-redux';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';

import { exitModal, exportReady, exportFailure } from '@commons/utils/exportModalWithProgress';
import ExportModalContent from '@lib/inpulse/ExportModal';

import { getClientInfo } from '@selectors/client';

import { product as productService } from '@services/product';
import recipeService from '@services/recipe';

import { exportProductsByStore } from '../../utils/exportProductsByStore';
import { loadProductsByBatchOfIds } from '../ProductExportModal';

const BATCH_PRODUCTS_TO_FETCH_AT_ONCE = 50;

export const ExportProductByStoreProgressModal = (props) => {
  const {
    params: { title, products, customParams, selectedStatus, selectedStores },
    client: { hasMultipleBrands, defaultChannelId, hasSimpleModifierManagement, clientChannels },
    currency,
    clientBrands,
    closeModal,
  } = props;

  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [titleModal, setTitle] = useState(title || '');
  const [productIdsToProcess, setProductIds] = useState([]);
  const [rawProductDataList, setRawProductDataList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [allProductIds, setAllProductIds] = useState([]);
  const [fetchingPhase, setFetchingPhase] = useState('initial');

  const fetchNextBatch = async () => {
    try {
      const { data, count } = await productService.getPaginatedProductsOfClient(
        {
          orderBy: 'name',
          orderType: 'asc',
          page: currentPage,
          limit: BATCH_PRODUCTS_TO_FETCH_AT_ONCE,
          search: '',
        },
        {
          brandId: customParams.brandId,
          active: { eq: get(selectedStatus, 'filterValue', false) },
          categoryId: customParams.categoryId,
          isIngredient: customParams.isIngredient,
          subCategoryId: customParams.subCategoryId,
          priceWithTaxes: customParams.priceWithTaxes,
          nbCashierProductMappingCount: customParams.nbCashierProductMappingCount,
        },
      );

      const productIds = (data || []).map(({ id }) => id);
      const updatedAllProductIds = [...allProductIds, ...productIds];

      setAllProductIds(updatedAllProductIds);

      if (
        data &&
        data.length === BATCH_PRODUCTS_TO_FETCH_AT_ONCE &&
        updatedAllProductIds.length < count
      ) {
        setCurrentPage(currentPage + 1);

        return;
      }

      setProductIds([...updatedAllProductIds]);
      setFetchingPhase('processing_details');
    } catch {
      exportFailure(setLoading, setTitle);
    }
  };

  // Init: export the selected products directly, or fetch all product ids by filters
  useEffect(() => {
    if (products && products.length) {
      setProductIds(products.map(({ id }) => id));
      setFetchingPhase('processing_details');

      return;
    }

    if (customParams && selectedStatus) {
      setFetchingPhase('fetching_ids');
    }
  }, []);

  useEffect(() => {
    if (fetchingPhase !== 'fetching_ids') {
      return;
    }

    fetchNextBatch();
  }, [currentPage, fetchingPhase]);

  // Fetch the product export data in batches (accumulates raw data)
  useEffect(() => {
    if (fetchingPhase !== 'processing_details') {
      return;
    }

    loadProductsByBatchOfIds(
      [...productIdsToProcess],
      rawProductDataList,
      setProgress,
      setProductIds,
      setRawProductDataList,
      setFetchingPhase,
      setLoading,
      setTitle,
    );
  }, [productIdsToProcess, fetchingPhase]);

  // Resolve the per-store variant costs of the exported recipes, then generate the XLS
  useEffect(() => {
    if (fetchingPhase !== 'finalizing') {
      return;
    }

    const finalize = async () => {
      try {
        const recipeIds = [
          ...new Set(
            rawProductDataList.flatMap(({ recipes }) =>
              (recipes || []).filter(({ isIngredient }) => !isIngredient).map(({ id }) => id),
            ),
          ),
        ];

        const storeIds = selectedStores.map(({ id }) => id);

        const storeVariantData = recipeIds.length
          ? await recipeService.getStoreVariantCompositions(recipeIds, storeIds)
          : { compositions: [], storeResolutions: [] };

        exportProductsByStore({
          rawProductDataList,
          clientChannels,
          defaultChannelId,
          clientBrands,
          hasMultipleBrands,
          hasSimpleModifierManagement,
          currency,
          selectedStores,
          storeVariantData,
        });

        exportReady(setLoading, setTitle);
      } catch {
        exportFailure(setLoading, setTitle);
      }
    };

    finalize();
  }, [fetchingPhase]);

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setLoading}
      titleModal={titleModal}
    />
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  clientBrands: state.baseReducer.brands,
  currency: state.baseReducer.currency,
});

export default connect(mapStateToProps, null)(ExportProductByStoreProgressModal);
